const API_PATH = "/auth/account-caseflow/";
const SOURCES = new Set(["signup", "new_matter"]);
const DESTINATIONS = new Set(["1", "2", "3", "4", "5"]);
const WORKSPACE_INTENTS = new Set(["personal", "firm", "founder", "investor"]);
const CASEFLOW_QUERY_KEYS = new Set(["stage", "step"]);

export const normalizeAccountCaseflowReturnTo = (returnTo, destinationAppId) => {
  if (typeof returnTo !== "string" || !returnTo.trim() || returnTo.length > 512) return null;
  const rawPath = returnTo.trim();
  if (!rawPath.startsWith("/") || rawPath.startsWith("//") || rawPath.includes("\\")) return null;

  let parsed;
  try {
    parsed = new URL(rawPath, "https://juristai.org");
  } catch (_error) {
    return null;
  }
  const destination = String(destinationAppId || "");
  if (parsed.origin !== "https://juristai.org" || parsed.hash || !DESTINATIONS.has(destination)) return null;
  const canonicalPath = destination === "5" ? "/caseflow" : `/dashboard/${destination}/caseflow`;
  const legacyPath = `/dashboard/${destination}/caseflow`;
  if (parsed.pathname !== "/caseflow" && parsed.pathname !== legacyPath) return null;

  const params = new URLSearchParams();
  for (const [key, value] of parsed.searchParams) {
    if (!CASEFLOW_QUERY_KEYS.has(key) || params.has(key) || !value || value.length > 80) return null;
    if (key === "stage" && !/^[A-Za-z0-9:-]+$/.test(value)) return null;
    if (key === "step" && !/^[A-Za-z][A-Za-z0-9_-]*$/.test(value)) return null;
    params.set(key, value);
  }

  const search = params.toString();
  return `${canonicalPath}${search ? `?${search}` : ""}`;
};

export const isValidAccountCaseflowContext = (context) =>
  Boolean(
    context &&
      typeof context === "object" &&
      context.version === 1 &&
      SOURCES.has(context.source) &&
      DESTINATIONS.has(String(context.destinationAppId || "")) &&
      (!context.workspaceIntent || WORKSPACE_INTENTS.has(context.workspaceIntent)) &&
      (context.returnTo == null || normalizeAccountCaseflowReturnTo(context.returnTo, context.destinationAppId) === context.returnTo),
  );

export const buildAccountCaseflowSearch = (context) => {
  if (!isValidAccountCaseflowContext(context)) return "";

  const params = new URLSearchParams({
    source: context.source,
    destinationAppId: String(context.destinationAppId),
  });
  if (context.workspaceIntent) params.set("workspaceIntent", context.workspaceIntent);
  if (context.returnTo) params.set("returnTo", context.returnTo);
  return `?${params.toString()}`;
};

export const buildAccountCaseflowPath = (context) => {
  if (!isValidAccountCaseflowContext(context)) return "";
  const destination = String(context.destinationAppId);
  const caseflowPath = destination === "5" ? "/caseflow" : `/dashboard/${destination}/caseflow`;
  return `${caseflowPath}${buildAccountCaseflowSearch(context)}`;
};

export const accountCaseflowResumeHintKey = (userId) => {
  const id = String(userId || "").trim();
  return id ? `juristai:account-caseflow:resume:${encodeURIComponent(id)}` : "";
};

export const createAccountCaseflowContextApi = (httpClient) => ({
  resolveAccess: async () => {
    const response = await httpClient.post(`${API_PATH}access/resolve/`);
    return response?.data ?? { success: false };
  },
  get: async () => {
    const response = await httpClient.get(API_PATH, { cache: false });
    return response?.data?.context ?? null;
  },
  save: async (context) => {
    const response = await httpClient.put(API_PATH, context);
    return response?.data?.context ?? null;
  },
  clear: async () => httpClient.delete(API_PATH),
});
